import React from 'react';

import { Chip, Stack, Tooltip, Typography } from '@mui/material';

import { PromoRequestItem } from '../hooks/usePromoRequests';

type ItemWarningLevel = 'error' | 'warning' | 'info' | 'debug';

type ItemWarning = {
  code: string;
  level: ItemWarningLevel;
  message?: string;
  transactionKey?: string;
};

const ITEM_WARNING_LEVEL_ORDER: Record<ItemWarningLevel, number> = {
  error: 0,
  warning: 1,
  info: 2,
  debug: 3,
};

const ITEM_WARNING_CHIP_COLOR: Record<ItemWarningLevel, 'error' | 'warning' | 'info' | 'default'> = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  debug: 'default',
};

function normalizeItemWarningLevel(level: unknown): ItemWarningLevel {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'debug') {
    return level;
  }
  return 'debug';
}

function getItemWarnings(row: PromoRequestItem): Array<ItemWarning> {
  const transactionKey = row.transactionKey?.trim();
  if (!transactionKey) {
    return [
      {
        code: 'missing-transaction-key',
        level: 'error',
        message: 'transactionKey is the primary required matching key for the item, it is required',
        transactionKey: undefined,
      },
    ];
  }

  const warnings: Array<ItemWarning> = [];
  const addWarning = (code: unknown, level: unknown, message?: unknown, key?: unknown) => {
    const normalizedCode = typeof code === 'string' ? code.trim() : '';
    if (!normalizedCode) {
      return;
    }

    const normalizedTransactionKey = typeof key === 'string' ? key.trim() : transactionKey;
    if (normalizedTransactionKey !== transactionKey) {
      return;
    }

    warnings.push({
      code: normalizedCode,
      level: normalizeItemWarningLevel(level),
      message: typeof message === 'string' && message.trim() ? message : undefined,
      transactionKey: normalizedTransactionKey,
    });
  };

  if (row.warningCode) {
    addWarning(row.warningCode, 'warning');
  }

  if (row.discountItem?.warning) {
    addWarning(row.discountItem.warning, 'warning');
  }

  if (row.statusMessage) {
    addWarning(row.statusMessage, 'info');
  }

  const metadata = row.logMetadata as
    | {
        warnings?: Array<{
          code?: string;
          level?: ItemWarningLevel;
          severity?: ItemWarningLevel;
          message?: string;
          transactionKey?: string;
          transaction_key?: string;
        }>;
      }
    | undefined;

  metadata?.warnings?.forEach((warning) => {
    addWarning(
      warning.code,
      warning.level ?? warning.severity,
      warning.message,
      warning.transactionKey ?? warning.transaction_key,
    );
  });

  return warnings
    .sort((a, b) => {
      const levelDiff = ITEM_WARNING_LEVEL_ORDER[a.level] - ITEM_WARNING_LEVEL_ORDER[b.level];
      if (levelDiff !== 0) {
        return levelDiff;
      }
      return a.code.localeCompare(b.code);
    })
    .filter((warning, index, list) => index === list.findIndex((candidate) => candidate.code === warning.code));
}

function ItemWarningsCell({ row }: { row: PromoRequestItem }) {
  const warnings = getItemWarnings(row);

  if (warnings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="column" spacing={0.5}>
      {warnings.map((warning) => (
        <Tooltip key={`${warning.level}-${warning.code}`} title={warning.message ?? warning.code} arrow>
          <Chip
            size="small"
            label={warning.code}
            color={ITEM_WARNING_CHIP_COLOR[warning.level]}
            variant={warning.level === 'debug' ? 'outlined' : 'filled'}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

export default ItemWarningsCell;
